/**
 * @fileoverview System-level visualization components
 * 
 * Contains components for:
 * - HeatPumpUnit: External unit with animated fan
 * - SystemParameters: Global system settings panel
 * - BuildingEdge: Building outline visualization
 * - Toolbar: Simulation control buttons
 * 
 * @module HydraulicBalancing/components/SystemComponents
 * @author HeatCraftHP Team
 * @version 2.0.0
 */

import React from 'react';
import { Space, Input, Button, Tooltip, Upload, Divider } from 'antd';
import {
  CaretRightOutlined,
  PauseOutlined,
  FullscreenOutlined,
  NodeIndexOutlined,
  OrderedListOutlined,
  InsertRowLeftOutlined,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';

import { LatexRenderer } from '../../../Shared/LatexRenderer';
import { COPMeter } from '../../../Shared/COPMeter';

import {
  SVG_DIMENSIONS,
  COLORS,
  OUTDOOR_CONFIG,
  HEAT_PUMP_CONFIG,
  PRESSURE_CONFIG,
} from '../constants';

import { formatNumber } from '../utils';

// Styles
import styles from '../HydraulicBalancing.module.css';

// Import assets (local to HydraulicBalancing folder)
import externalUnitHeatPump from '../externalUnitHeatPump.png';
import externalUnitHeatPumpFan from '../externalUnitHeatPumpFan.gif';
import outdoorWeatherIcon from '../outdoorWeather.png';
import HeatCraftHPLogo from '../HeatCraftHPLogo.png';

const { GROUND_Y } = SVG_DIMENSIONS;

// ============================================================================
// HEAT PUMP UNIT COMPONENT
// ============================================================================

/**
 * SVG visualization of the external heat pump unit
 * 
 * Features:
 * - Heat pump body with condenser coils
 * - Animated fan when system is running
 * - Supply and return pipe connections
 * - Pressure and temperature indicators
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isRunning - Whether simulation is active
 * @param {boolean} props.hasFlow - Whether water is flowing
 * @param {number} props.systemPressure - System differential pressure [bar]
 * @param {number} props.tSupply - Supply temperature [°C]
 * @param {number} props.tReturn - Return temperature [°C]
 * @param {number} props.cop - Coefficient of Performance
 */
export const HeatPumpUnit = React.memo(({
  isRunning,
  hasFlow,
  systemPressure,
  tSupply,
  tReturn,
  cop,
}) => {
  const fanActive = isRunning && hasFlow;
  
  return (
    <g>
      {/* Ground line */}
      <rect
        x={15}
        y={GROUND_Y}
        width={735}
        height={10}
        fill="rgba(0, 0, 0, 0.3)"
      />
      
      {/* HeatCraftHP Logo */}
      <image
        href={HeatCraftHPLogo}
        x={15}
        y={705}
        width={200}
      />
      
      {/* Heat pump external unit */}
      <image
        href={externalUnitHeatPump}
        width={65}
        x={20}
        y={628}
      />
      
      {/* Animated fan - only when running with flow */}
      {fanActive && (
        <image
          href={externalUnitHeatPumpFan}
          width={31}
          x={25}
          y={633}
        />
      )}
      
      {/* Return pipe (cold - to heat pump) */}
      <g>
        <rect x={35} y={580} height={48} width={8} fill={COLORS.RETURN} />
        <rect x={37} y={560} height={5} width={3} fill={COLORS.RETURN} />
        <rect x={37} y={570} height={5} width={3} fill={COLORS.RETURN} />
      </g>
      
      {/* Supply pipe (hot - from heat pump) */}
      <g>
        <rect x={60} y={580} height={48} width={8} fill={COLORS.SUPPLY} />
        <rect x={62} y={560} height={5} width={3} fill={COLORS.SUPPLY} />
        <rect x={62} y={570} height={5} width={3} fill={COLORS.SUPPLY} />
      </g>
      
      {/* System pressure indicator */}
      <text x={32} y={555} fill="rgba(0, 0, 0, 0.8)" fontSize={12}>
        {hasFlow ? systemPressure : 0} bar
      </text>
      
      {/* Temperature indicators */}
      <text x={20} y={540} fill={COLORS.HOT} fontSize={12}>
        {formatNumber(tSupply, 0)}
      </text>
      <text x={40} y={540} fill={COLORS.RETURN_STRONG} fontSize={12}>
        {formatNumber(tReturn, 1)} °C
      </text>
      
      {/* COP display */}
      <COPMeter
        cop={cop}
        x={720}
        y={28}
        fontSize={16}
      />
    </g>
  );
});

HeatPumpUnit.displayName = 'HeatPumpUnit';

// ============================================================================
// OUTDOOR CONDITIONS DISPLAY
// ============================================================================

/**
 * Displays current outdoor temperature with weather icon
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.temperature - Outdoor temperature [°C]
 */
export const OutdoorConditions = React.memo(({ temperature }) => (
  <Tooltip 
    title={
      <div>
        <p><strong>Outdoor Air Temperature</strong></p>
        <p>Current ambient temperature affecting heat pump performance.</p>
        <p>Lower outdoor temperatures reduce heat pump COP efficiency.</p>
      </div>
    }
    color="white"
    overlayStyle={{ minWidth: '420px' }}
  >
    <g style={{ cursor: 'pointer' }}>
      <image
        href={outdoorWeatherIcon}
        width={25}
        x={10}
        y={10}
      />
      <text x={40} y={28} fill={COLORS.HOT} fontSize={16}>
        {formatNumber(temperature, 0)} °C
      </text>
    </g>
  </Tooltip>
));

OutdoorConditions.displayName = 'OutdoorConditions';

// ============================================================================
// BUILDING EDGE OUTLINE
// ============================================================================

/**
 * Renders the building outline based on room positions
 * Creates a continuous line around the occupied rooms
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array<Array<Object>>} props.rooms - Building grid
 */
export const BuildingEdge = React.memo(({ rooms }) => {
  // Generate edge segments for each room
  const edges = [];
  const { ROOM_WIDTH, ROOM_HEIGHT, MARGIN_LEFT, GROUND_Y } = SVG_DIMENSIONS;
  
  rooms.forEach((floor, floorIndex) => {
    floor.forEach((room, colIndex) => {
      if (!room) return;
      
      const x = MARGIN_LEFT + ROOM_WIDTH * colIndex;
      const y = GROUND_Y - ROOM_HEIGHT * (floorIndex + 1);
      
      // Check neighbors to determine which edges to draw
      const hasAbove = rooms[floorIndex + 1]?.[colIndex];
      const hasBelow = rooms[floorIndex - 1]?.[colIndex];
      const hasLeft = colIndex > 0 && rooms[floorIndex]?.[colIndex - 1];
      const hasRight = rooms[floorIndex]?.[colIndex + 1];
      
      // Top edge (roof or ceiling)
      if (!hasAbove) {
        edges.push(
          <line
            key={`top-${floorIndex}-${colIndex}`}
            x1={x}
            y1={y}
            x2={x + ROOM_WIDTH}
            y2={y}
            stroke={COLORS.WALL}
            strokeWidth={2}
          />
        );
      }
      
      // Bottom edge (floor or foundation)
      if (!hasBelow && floorIndex > 0) {
        edges.push(
          <line
            key={`bottom-${floorIndex}-${colIndex}`}
            x1={x}
            y1={y + ROOM_HEIGHT}
            x2={x + ROOM_WIDTH}
            y2={y + ROOM_HEIGHT}
            stroke={COLORS.WALL}
            strokeWidth={2}
          />
        );
      }
      
      // Left edge (exterior wall)
      if (!hasLeft) {
        edges.push(
          <line
            key={`left-${floorIndex}-${colIndex}`}
            x1={x}
            y1={y}
            x2={x}
            y2={y + ROOM_HEIGHT}
            stroke={COLORS.WALL}
            strokeWidth={2}
          />
        );
      }
      
      // Right edge (exterior wall)
      if (!hasRight) {
        edges.push(
          <line
            key={`right-${floorIndex}-${colIndex}`}
            x1={x + ROOM_WIDTH}
            y1={y}
            x2={x + ROOM_WIDTH}
            y2={y + ROOM_HEIGHT}
            stroke={COLORS.WALL}
            strokeWidth={2}
          />
        );
      }
    });
  });
  
  return <g className="building-edge">{edges}</g>;
});

BuildingEdge.displayName = 'BuildingEdge';

// ============================================================================
// SIMULATION TOOLBAR
// ============================================================================

/**
 * Toolbar with simulation controls and visualization toggles
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isRunning - Whether simulation is running
 * @param {Function} props.onToggleRun - Toggle run state
 * @param {Object} props.overlays - Overlay visibility states
 * @param {Function} props.onToggleOverlay - Toggle overlay visibility
 * @param {Function} props.onExport - Export configuration
 * @param {Function} props.onImport - Import configuration handler
 * @param {boolean} props.isLoading - Whether import is in progress
 */
export const SimulationToolbar = ({
  isRunning,
  onToggleRun,
  overlays,
  onToggleOverlay,
  onExport,
  onImport,
  isLoading,
}) => {
  // Dummy request for Upload component
  const dummyRequest = ({ onSuccess }) => {
    setTimeout(() => onSuccess('ok'), 0);
  };

  return (
    <Space direction="horizontal" size="small">
      {/* Play/Pause button */}
      <Button
        type={isRunning ? "primary" : "default"}
        size="large"
        icon={isRunning ? <PauseOutlined /> : <CaretRightOutlined />}
        onClick={onToggleRun}
        title={isRunning ? "Pause Simulation" : "Start Simulation"}
        style={{ width: "40px", height: "40px"}}
      />

      <Divider type="vertical" />
      
      {/* Visualization toggles */}
      <Button
        type={overlays.showRadiatorRating ? 'primary' : 'default'}
        size="large"
        icon={<FullscreenOutlined />}
        onClick={() => onToggleOverlay('radiator')}
        title="Toggle Radiator Rating Display"
        style={{ width: "40px", height: "40px"}}
      />
      
      <Button
        type={overlays.showHeatFlux ? 'primary' : 'default'}
        size="large"
        icon={<NodeIndexOutlined />}
        onClick={() => onToggleOverlay('heatFlux')}
        title="Toggle Heat Flux Visualization"
        style={{ width: "40px", height: "40px"}}
      />
      
      <Button
        type={overlays.showTemperatureRating ? 'primary' : 'default'}
        size="large"
        icon={<OrderedListOutlined />}
        onClick={() => onToggleOverlay('temperature')}
        title="Toggle Temperature Rating"
        style={{ width: "40px", height: "40px"}}
      />
      
      <Button
        type={overlays.showUValueRating ? 'primary' : 'default'}
        size="large"
        icon={<InsertRowLeftOutlined />}
        onClick={() => onToggleOverlay('uValue')}
        title="Toggle U-value Rating"
        style={{ width: "40px", height: "40px"}}
      />
      
      <Divider type="vertical" />
      
      {/* Export/Import buttons */}
      <Button
        size="large"
        icon={<DownloadOutlined />}
        onClick={onExport}
        title="Export Configuration"
        style={{ width: "40px", height: "40px" }}
      />
      
      <Upload
        accept=".json"
        onChange={onImport}
        customRequest={dummyRequest}
        showUploadList={false}
      >
        <Button
          size="large"
          loading={isLoading}
          icon={<UploadOutlined />}
          title="Import Configuration"
          style={{ width: "40px", height: "40px" }}
        />
      </Upload>
    </Space>
  );
};

// ============================================================================
// SYSTEM PARAMETERS PANEL
// ============================================================================

/**
 * Panel for adjusting global system parameters
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.params - Current parameter values
 * @param {Object} props.setters - Parameter setter functions
 */
export const SystemParametersPanel = ({ params, setters }) => {
  const {
    tSupply,
    tOutdoor,
    uRoomToRoom,
    systemPressure,
    kvMax,
    kRad,
    pipeLength,
    pipeDiameter,
    airChangeRate,
  } = params;
  
  const {
    setTSupply,
    setTOutdoor,
    setURoomToRoom,
    setSystemPressure,
    setKvMax,
    setKRad,
    setPipeLength,
    setPipeDiameter,
    setAirChangeRate,
  } = setters;

  return (
    <div className={styles.parametersPanel}>
      <Space direction="vertical" size="small" style={{ width: '100%' }} align="start">
        {/* Row 1 – Temperatures & Thermal */}
        <Space direction="horizontal" size="large" wrap>
          <Space direction="vertical" align="start" size="small">
            <small className="default-title">Supply temperature</small>
            <Input
              type="number"
              min={HEAT_PUMP_CONFIG.MIN_SUPPLY_TEMP}
              max={HEAT_PUMP_CONFIG.MAX_SUPPLY_TEMP}
              value={tSupply}
              style={{ width: 160 }}
              suffix="°C"
              onChange={(e) => setTSupply(parseFloat(e.target.value) || 55)}
            />
          </Space>

          <Space direction="vertical" align="start" size="small">
            <small className="default-title">Outdoor air temperature</small>
            <Input
              type="number"
              min={OUTDOOR_CONFIG.MIN_OUTDOOR_TEMP}
              max={OUTDOOR_CONFIG.MAX_OUTDOOR_TEMP}
              value={tOutdoor}
              style={{ width: 160 }}
              suffix="°C"
              onChange={(e) => setTOutdoor(parseFloat(e.target.value) || 5)}
            />
          </Space>

          <Space direction="vertical" align="start" size="small">
            <small className="default-title">Inter-room U‑value</small>
            <Input
              type="number"
              min={0.05}
              max={1}
              step={0.05}
              value={uRoomToRoom}
              style={{ width: 160 }}
              suffix={<LatexRenderer latex="$ \frac{W}{m^2 \cdot K} $" />}
              onChange={(e) => setURoomToRoom(parseFloat(e.target.value) || 0.15)}
            />
          </Space>

          <Space direction="vertical" align="start" size="small">
            <small className="default-title">Radiator output coefficient</small>
            <Input
              type="number"
              min={1}
              max={20}
              step={1}
              value={kRad}
              style={{ width: 160 }}
              suffix={<LatexRenderer latex="$ \frac{W}{K^n} $" />}
              onChange={(e) => setKRad(parseFloat(e.target.value) || 8)}
            />
          </Space>

          <Space direction="vertical" align="start" size="small">
            <small className="default-title">Air change rate</small>
            <Input
              type="number"
              min={0.25}
              max={10}
              step={0.25}
              value={airChangeRate}
              style={{ width: 160 }}
              suffix="ACH"
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0.5;
                if (value >= 0.25 && value <= 10) {
                  setAirChangeRate(value);
                }
              }}
            />
          </Space>
        </Space>

        <div style={{ width: "100%", height: 1, backgroundColor: "rgba(0, 0, 0, 0.2)" }} />

        {/* Row 2 – Hydraulics & Piping */}
        <Space direction="horizontal" size="large" wrap>
          <Space direction="vertical" align="start" size="small">
            <small className="default-title">System differential pressure</small>
            <Input
              type="number"
              min={PRESSURE_CONFIG.MIN_DP}
              max={PRESSURE_CONFIG.MAX_DP}
              step={0.05}
              value={systemPressure}
              style={{ width: 160 }}
              suffix="bar"
              onChange={(e) => setSystemPressure(parseFloat(e.target.value) || 0.3)}
            />
          </Space>

          <Space direction="vertical" align="start" size="small">
            <small className="default-title">Preset valve capacity Kᵥ, max</small>
            <Input
              type="number"
              min={0.3}
              max={1.5}
              step={0.1}
              value={kvMax}
              style={{ width: 160 }}
              suffix={<LatexRenderer latex="$ \frac{m^3}{h \cdot \sqrt{bar}} $" />}
              onChange={(e) => setKvMax(parseFloat(e.target.value) || 0.5)}
            />
          </Space>

          <Space direction="vertical" align="start" size="small">
            <small className="default-title">Pipe length per branch</small>
            <Input
              type="number"
              min={3}
              max={15}
              step={0.5}
              value={pipeLength}
              style={{ width: 160 }}
              suffix="m"
              onChange={(e) => setPipeLength(parseFloat(e.target.value) || 5)}
            />
          </Space>

          <Space direction="vertical" align="start" size="small">
            <small className="default-title">Pipe inner diameter</small>
            <Input
              type="number"
              min={10}
              max={25}
              step={1}
              value={pipeDiameter}
              style={{ width: 160 }}
              suffix="mm"
              onChange={(e) => setPipeDiameter(parseFloat(e.target.value) || 15)}
            />
          </Space>
        </Space>
      </Space>
    </div>
  );
};

export default HeatPumpUnit;
